import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  adminApi,
  subscriptionApi,
  AdminTenant,
  CreateTenantPayload,
  UpdateTenantPayload,
  TenantUsageStats,
  SubscriptionPlan,
  GrantFreeAccessPayload,
} from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import { useToast } from "../Toast";
import { SYSTEM_TENANT_NAME } from "../../config/constants";
import { formatBillingDate, formatActivityDate } from "../../utils/formatters";
import BusinessIcon from "@mui/icons-material/Business";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import "./TenantManagement.css";

type StatusFilter = "all" | "active" | "inactive";

// Helper to get usage bar color based on percentage
const getUsageColor = (used: number, limit: number | null): string => {
  if (limit === null || limit === 0) return "#4299e1"; // Blue for unlimited/no limit
  const percentage = (used / limit) * 100;
  if (percentage >= 90) return "#c53030"; // Red
  if (percentage >= 70) return "#d69e2e"; // Yellow/Orange
  return "#38a169"; // Green
};

// Helper to get billing status badge
const getBillingStatusBadge = (
  subscriptionStatus: string | null | undefined,
  isSystemTenant: boolean
): React.ReactElement => {
  if (isSystemTenant) {
    return <span className="billing-badge exempt">Exempt</span>;
  }
  switch (subscriptionStatus) {
    case "active":
      return <span className="billing-badge active">Active</span>;
    case "trialing":
      return <span className="billing-badge active">Free Trial</span>;
    case "past_due":
      return <span className="billing-badge past-due">Past Due</span>;
    case "paused":
      return <span className="billing-badge canceled">Canceled</span>;
    case "canceled":
      return <span className="billing-badge canceled">Canceled</span>;
    default:
      return <span className="billing-badge no-billing">No Subscription</span>;
  }
};

// Usage progress bar component
const UsageBar: React.FC<{ used: number; limit: number | null; label: string }> = ({
  used,
  limit,
  label,
}) => {
  const isUnlimited = limit === null;
  const percentage = limit && limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const color = getUsageColor(used, limit);

  return (
    <div className="usage-bar-container" title={`${label}: ${used}/${isUnlimited ? '∞' : limit}`}>
      <div className="usage-bar-label">
        <span>{label}</span>
        <span style={{ color }}>
          {used}/{isUnlimited ? '∞' : limit}
        </span>
      </div>
      <div className="usage-bar-track">
        <div
          className="usage-bar-fill"
          style={{
            width: isUnlimited ? '100%' : `${percentage}%`,
            backgroundColor: color,
            opacity: isUnlimited ? 0.3 : 1,
          }}
        />
      </div>
    </div>
  );
};

const TenantManagement: React.FC = () => {
  const { showToast } = useToast();
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<AdminTenant | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [confirmationTenant, setConfirmationTenant] = useState<AdminTenant | null>(null);
  const [confirmationAction, setConfirmationAction] = useState<
    "activate" | "deactivate" | null
  >(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  // Form state
  const [formData, setFormData] = useState<CreateTenantPayload>({
    name: "",
    is_active: true,
  });
  const [updateFormData, setUpdateFormData] = useState<UpdateTenantPayload>({});
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Grant free access state
  const [showGrantAccessModal, setShowGrantAccessModal] = useState(false);
  const [grantAccessTenant, setGrantAccessTenant] = useState<AdminTenant | null>(null);
  const [availablePlans, setAvailablePlans] = useState<SubscriptionPlan[]>([]);
  const [grantAccessFormData, setGrantAccessFormData] = useState<GrantFreeAccessPayload>({
    plan_id: "",
    duration_months: null, // null = forever
  });
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);

  // Revoke access confirmation state
  const [revokeConfirmTenant, setRevokeConfirmTenant] = useState<AdminTenant | null>(null);
  const [isRevokeConfirmOpen, setIsRevokeConfirmOpen] = useState(false);

  // Refs to prevent duplicate fetches
  const isFetchingRef = useRef(false);
  const hasFetchedRef = useRef(false);

  const loadTenants = useCallback(async (forceRefresh = false) => {
    // Prevent concurrent fetches
    if (isFetchingRef.current) return;
    // Skip if already fetched unless force refresh
    if (hasFetchedRef.current && !forceRefresh) return;
    
    isFetchingRef.current = true;
    setIsLoading(true);
    setError("");
    try {
      const tenantsData = await adminApi.listTenants();
      setTenants(tenantsData);
      hasFetchedRef.current = true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load tenants");
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      loadTenants();
    }
  }, [loadTenants]);

  // Filter tenants based on status
  const filteredTenants = useMemo(() => {
    if (statusFilter === "all") return tenants;
    return tenants.filter((tenant) =>
      statusFilter === "active" ? tenant.is_active : !tenant.is_active
    );
  }, [tenants, statusFilter]);

  const handleCreateTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    setIsSubmitting(true);

    try {
      await adminApi.createTenant(formData);
      setShowCreateModal(false);
      setFormData({ name: "", is_active: true });
      loadTenants(true);
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Failed to create tenant"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTenant) return;

    setFormError("");
    setIsSubmitting(true);

    try {
      await adminApi.updateTenant(selectedTenant.id, updateFormData);
      setShowEditModal(false);
      setSelectedTenant(null);
      setUpdateFormData({});
      loadTenants(true);
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Failed to update tenant"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleTenantStatus = (tenant: AdminTenant) => {
    const action = tenant.is_active ? "deactivate" : "activate";
    setConfirmationTenant(tenant);
    setConfirmationAction(action);
    setIsConfirmationOpen(true);
  };

  const handleConfirmToggleTenantStatus = async () => {
    if (!confirmationTenant || !confirmationAction) return;
    const action = confirmationAction;
    try {
      if (confirmationTenant.is_active) {
        await adminApi.deleteTenant(confirmationTenant.id);
      } else {
        await adminApi.updateTenant(confirmationTenant.id, { is_active: true });
      }
      loadTenants(true);
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : `Failed to ${action} tenant`
      );
    } finally {
      setIsConfirmationOpen(false);
      setConfirmationTenant(null);
      setConfirmationAction(null);
    }
  };

  const openEditModal = (tenant: AdminTenant) => {
    setSelectedTenant(tenant);
    setUpdateFormData({
      name: tenant.name,
      is_active: tenant.is_active,
      serp_preview_enabled: tenant.serp_preview_enabled,
    });
    setFormError("");
    setShowEditModal(true);
  };

  // Load subscription plans for grant access modal
  const loadPlansForGrant = async () => {
    setIsLoadingPlans(true);
    try {
      const plans = await subscriptionApi.listPlans();
      setAvailablePlans(plans.filter(p => p.is_active));
      if (plans.length > 0) {
        setGrantAccessFormData(prev => ({ ...prev, plan_id: plans[0].id }));
      }
    } catch (err) {
      showToast("error", "Failed to load subscription plans");
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const openGrantAccessModal = (tenant: AdminTenant) => {
    setGrantAccessTenant(tenant);
    setGrantAccessFormData({ plan_id: "", duration_months: null });
    setFormError("");
    setShowGrantAccessModal(true);
    loadPlansForGrant();
  };

  const handleGrantAccess = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!grantAccessTenant || !grantAccessFormData.plan_id) return;

    setFormError("");
    setIsSubmitting(true);

    try {
      const response = await adminApi.grantFreeAccess(
        grantAccessTenant.id,
        grantAccessFormData
      );
      showToast("success", response.message);
      setShowGrantAccessModal(false);
      setGrantAccessTenant(null);
      loadTenants(true);
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : "Failed to grant free access"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const openRevokeAccessConfirm = (tenant: AdminTenant) => {
    setRevokeConfirmTenant(tenant);
    setIsRevokeConfirmOpen(true);
  };

  const handleRevokeAccess = async () => {
    if (!revokeConfirmTenant) return;

    try {
      await adminApi.revokeFreeAccess(revokeConfirmTenant.id);
      showToast("success", `Access revoked from ${revokeConfirmTenant.name}`);
      loadTenants(true);
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to revoke access"
      );
    } finally {
      setIsRevokeConfirmOpen(false);
      setRevokeConfirmTenant(null);
    }
  };

  // Check if tenant has free access (active subscription without Stripe)
  const hasFreeAccess = (tenant: AdminTenant): boolean => {
    return (
      tenant.subscription_status === "active" &&
      !tenant.stripe_customer_id &&
      tenant.name !== SYSTEM_TENANT_NAME
    );
  };

  // Check if tenant can be granted free access
  const canGrantFreeAccess = (tenant: AdminTenant): boolean => {
    return (
      tenant.name !== SYSTEM_TENANT_NAME &&
      tenant.subscription_status !== "active" &&
      tenant.subscription_status !== "trialing"
    );
  };

  const renderUsageStats = (usage: TenantUsageStats | null | undefined, tenantName?: string) => {
    if (!usage) {
      return <span className="no-plan-badge">No plan</span>;
    }

    // Check if this is the system tenant - unlimited access
    if (usage.is_unlimited || tenantName === SYSTEM_TENANT_NAME) {
      return (
        <div className="usage-stats-compact">
          <span className="unlimited-badge">∞ Unlimited</span>
          <div style={{ fontSize: '11px', color: '#718096', marginTop: '4px' }}>
            Domains: {usage.domains_used} | Clusters: {usage.clusters_used} | Users: {usage.users_used}
          </div>
        </div>
      );
    }

    return (
      <div className="usage-stats-compact">
        <UsageBar
          used={usage.domains_used}
          limit={usage.domains_limit}
          label="Domains"
        />
        <UsageBar
          used={usage.clusters_used}
          limit={usage.clusters_limit}
          label="Clusters"
        />
        <UsageBar
          used={usage.users_used}
          limit={usage.users_limit}
          label="Users"
        />
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-spinner"></div>
        <p>Loading tenants...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        {error}
        <button onClick={() => loadTenants(true)} style={{ marginLeft: 16 }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-section-header">
        <h2>Tenant Management</h2>
        <div className="admin-header-actions">
          <div className="admin-filter-group">
            <label>Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="admin-filter-select"
            >
              <option value="all">All ({tenants.length})</option>
              <option value="active">
                Active ({tenants.filter((t) => t.is_active).length})
              </option>
              <option value="inactive">
                Inactive ({tenants.filter((t) => !t.is_active).length})
              </option>
            </select>
          </div>
          <button
            className="admin-add-btn"
            onClick={() => {
              setFormError("");
              setShowCreateModal(true);
            }}
          >
            <span>+</span> Add Tenant
          </button>
        </div>
      </div>

      {filteredTenants.length === 0 ? (
        <div className="admin-empty">
          <BusinessIcon className="admin-empty-icon" sx={{ fontSize: 48 }} />
          <p>
            {statusFilter === "all"
              ? "No tenants found. Create your first tenant to get started."
              : `No ${statusFilter} tenants found.`}
          </p>
        </div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Plan</th>
                <th>Billing Status</th>
                <th>Usage</th>
                <th>Users</th>
                <th>Status</th>
                <th>Last Activity</th>
                <th>Next billing date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTenants.map((tenant) => (
                <tr key={tenant.id}>
                  <td>{tenant.name}</td>
                  <td>
                    {tenant.name === SYSTEM_TENANT_NAME ? (
                      <span className="unlimited-badge">System Tenant</span>
                    ) : tenant.subscription_plan_name ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span
                          className={`plan-badge ${tenant.subscription_plan_name.toLowerCase()}`}
                        >
                          {tenant.subscription_plan_name}
                        </span>
                        {tenant.billing_cycle && (
                          <span className={`billing-cycle-badge ${tenant.billing_cycle}`}>
                            {tenant.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'}
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="no-plan-badge">No plan</span>
                    )}
                  </td>
                  <td>
                    {getBillingStatusBadge(
                      tenant.subscription_status,
                      tenant.name === SYSTEM_TENANT_NAME
                    )}
                  </td>
                  <td>{renderUsageStats(tenant.usage_stats, tenant.name)}</td>
                  <td>{tenant.user_count}</td>
                  <td>
                    <span
                      className={`badge ${
                        tenant.is_active ? "badge-active" : "badge-inactive"
                      }`}
                    >
                      {tenant.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={{ whiteSpace: "nowrap", fontSize: "0.85rem", color: "#4a5568" }}>
                    {formatActivityDate(tenant.last_activity_at)}
                  </td>
                  <td>
                    {tenant.subscription_current_period_end
                      ? formatBillingDate(tenant.subscription_current_period_end)
                      : "-"}
                  </td>
                  <td>
                    <div className="admin-actions">
                      <button
                        className="admin-action-btn"
                        onClick={() => openEditModal(tenant)}
                      >
                        Edit
                      </button>
                      {canGrantFreeAccess(tenant) && (
                        <button
                          className="admin-action-btn grant"
                          onClick={() => openGrantAccessModal(tenant)}
                          title="Grant free subscription access"
                        >
                          <CardGiftcardIcon sx={{ fontSize: 14 }} /> Grant
                        </button>
                      )}
                      {hasFreeAccess(tenant) && (
                        <button
                          className="admin-action-btn revoke"
                          onClick={() => openRevokeAccessConfirm(tenant)}
                          title="Revoke free access"
                        >
                          Revoke
                        </button>
                      )}
                      <button
                        className={`admin-action-btn ${
                          tenant.is_active ? "delete" : "activate"
                        }`}
                        onClick={() => handleToggleTenantStatus(tenant)}
                      >
                        {tenant.is_active ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Tenant Modal */}
      {showCreateModal && (
        <div
          className="admin-modal-overlay"
          onClick={() => setShowCreateModal(false)}
        >
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Tenant</h3>
            {formError && <div className="admin-error">{formError}</div>}
            <form onSubmit={handleCreateTenant}>
              <div className="admin-form-group">
                <label>Tenant Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Acme Corporation"
                  required
                />
              </div>
              <div className="admin-form-group admin-form-checkbox">
                <input
                  type="checkbox"
                  id="tenant_is_active"
                  checked={formData.is_active}
                  onChange={(e) =>
                    setFormData({ ...formData, is_active: e.target.checked })
                  }
                />
                <label htmlFor="tenant_is_active">Active</label>
              </div>
              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-modal-btn secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-modal-btn primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create Tenant"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Tenant Modal */}
      {showEditModal && selectedTenant && (
        <div
          className="admin-modal-overlay"
          onClick={() => setShowEditModal(false)}
        >
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Tenant</h3>
            {formError && <div className="admin-error">{formError}</div>}
            <form onSubmit={handleEditTenant}>
              <div className="admin-form-group">
                <label>Tenant Name</label>
                <input
                  type="text"
                  value={updateFormData.name || ""}
                  onChange={(e) =>
                    setUpdateFormData({
                      ...updateFormData,
                      name: e.target.value,
                    })
                  }
                  placeholder="Acme Corporation"
                />
              </div>
              {selectedTenant.name !== SYSTEM_TENANT_NAME && selectedTenant.subscription_plan_name && (
                <div className="admin-form-group">
                  <label>Current Plan</label>
                  <div
                    style={{
                      padding: "12px",
                      background: "#f7fafc",
                      borderRadius: "8px",
                    }}
                  >
                    <span
                      className={`plan-badge ${selectedTenant.subscription_plan_name.toLowerCase()}`}
                    >
                      {selectedTenant.subscription_plan_name}
                    </span>
                    <span
                      style={{
                        marginLeft: "8px",
                        fontSize: "0.8rem",
                        color: "#718096",
                      }}
                    >
                      (Changed via billing page)
                    </span>
                  </div>
                </div>
              )}
              {selectedTenant.name === SYSTEM_TENANT_NAME && (
                <div className="admin-form-group">
                  <label>Plan Status</label>
                  <div
                    style={{
                      padding: "12px",
                      background: "#f7fafc",
                      borderRadius: "8px",
                    }}
                  >
                    <span className="unlimited-badge">System Tenant - Unlimited Access</span>
                  </div>
                </div>
              )}
              {selectedTenant.usage_stats && (
                <div className="admin-form-group">
                  <label>Current Usage</label>
                  <div
                    style={{
                      padding: "12px",
                      background: "#f7fafc",
                      borderRadius: "8px",
                    }}
                  >
                    {renderUsageStats(selectedTenant.usage_stats, selectedTenant.name)}
                  </div>
                </div>
              )}
              <div className="admin-form-group admin-form-checkbox">
                <input
                  type="checkbox"
                  id="edit_tenant_serp_preview"
                  checked={updateFormData.serp_preview_enabled ?? false}
                  onChange={(e) =>
                    setUpdateFormData({
                      ...updateFormData,
                      serp_preview_enabled: e.target.checked,
                    })
                  }
                />
                <label htmlFor="edit_tenant_serp_preview">SERP Preview (enterprise feature)</label>
              </div>
              <div className="admin-form-group admin-form-checkbox">
                <input
                  type="checkbox"
                  id="edit_tenant_is_active"
                  checked={updateFormData.is_active ?? true}
                  onChange={(e) =>
                    setUpdateFormData({
                      ...updateFormData,
                      is_active: e.target.checked,
                    })
                  }
                />
                <label htmlFor="edit_tenant_is_active">Active</label>
              </div>
              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-modal-btn secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-modal-btn primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Grant Free Access Modal */}
      {showGrantAccessModal && grantAccessTenant && (
        <div
          className="admin-modal-overlay"
          onClick={() => setShowGrantAccessModal(false)}
        >
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>
              <CardGiftcardIcon sx={{ fontSize: 24, marginRight: 1, verticalAlign: 'middle' }} />
              Grant Free Access
            </h3>
            <p style={{ color: '#718096', marginBottom: '1rem' }}>
              Grant free subscription access to <strong>{grantAccessTenant.name}</strong> without requiring payment.
            </p>
            {formError && <div className="admin-error">{formError}</div>}
            <form onSubmit={handleGrantAccess}>
              <div className="admin-form-group">
                <label>Subscription Plan</label>
                {isLoadingPlans ? (
                  <p>Loading plans...</p>
                ) : (
                  <select
                    value={grantAccessFormData.plan_id}
                    onChange={(e) =>
                      setGrantAccessFormData({
                        ...grantAccessFormData,
                        plan_id: e.target.value,
                      })
                    }
                    required
                  >
                    <option value="">Select a plan...</option>
                    {availablePlans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name} - {plan.max_domains} domains, {plan.max_clusters} clusters, {plan.max_users} users
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="admin-form-group">
                <label>Duration</label>
                <select
                  value={grantAccessFormData.duration_months === null ? "forever" : String(grantAccessFormData.duration_months)}
                  onChange={(e) => {
                    const value = e.target.value;
                    setGrantAccessFormData({
                      ...grantAccessFormData,
                      duration_months: value === "forever" ? null : parseInt(value, 10),
                    });
                  }}
                >
                  <option value="forever">Forever (no expiration)</option>
                  <option value="1">1 month</option>
                  <option value="3">3 months</option>
                  <option value="6">6 months</option>
                  <option value="12">12 months (1 year)</option>
                  <option value="24">24 months (2 years)</option>
                </select>
              </div>
              <div className="admin-form-group" style={{ background: '#f0fff4', padding: '12px', borderRadius: '8px', border: '1px solid #68d391' }}>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#276749' }}>
                  <strong>Note:</strong> This grants the tenant full access to the selected plan without requiring Stripe payment. 
                  Use for partners, beta testers, or promotional offers.
                </p>
              </div>
              <div className="admin-modal-actions">
                <button
                  type="button"
                  className="admin-modal-btn secondary"
                  onClick={() => setShowGrantAccessModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-modal-btn primary"
                  disabled={isSubmitting || !grantAccessFormData.plan_id}
                >
                  {isSubmitting ? "Granting..." : "Grant Access"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={() => {
          setIsConfirmationOpen(false);
          setConfirmationTenant(null);
          setConfirmationAction(null);
        }}
        onConfirm={handleConfirmToggleTenantStatus}
        title={
          confirmationAction === "deactivate"
            ? "Deactivate tenant"
            : "Activate tenant"
        }
        message={
          confirmationAction === "deactivate"
            ? "Are you sure you want to deactivate this tenant? Users of this tenant will not be able to log in."
            : "Are you sure you want to activate this tenant?"
        }
        confirmText={
          confirmationAction === "deactivate" ? "Deactivate" : "Activate"
        }
        variant={confirmationAction === "deactivate" ? "danger" : "info"}
      />

      <ConfirmationModal
        isOpen={isRevokeConfirmOpen}
        onClose={() => {
          setIsRevokeConfirmOpen(false);
          setRevokeConfirmTenant(null);
        }}
        onConfirm={handleRevokeAccess}
        title="Revoke Free Access"
        message={
          revokeConfirmTenant
            ? `Are you sure you want to revoke free access from "${revokeConfirmTenant.name}"? They will lose access to premium features.`
            : ""
        }
        confirmText="Revoke Access"
        variant="danger"
      />
    </div>
  );
};

export default TenantManagement;
